import React, { useState } from 'react';

/**
 * Graphes du cockpit, en HTML/SVG simple. Une seule teinte de série (bleu, validé sur fond sombre) :
 * le vert reste au temps passé et aux paiements réels, le sable aux actions.
 * Chaque marque a son infobulle au survol ; les valeurs exactes restent lisibles dans les listes à côté.
 */

/** Colonnes (arrivées par heure / par jour, répartition des durées). rows : [{ key, label, value, tip }] */
export function Columns({ rows, height = 160, labelEvery = 1, empty = 'Pas encore de donnée.' }) {
  const [hover, setHover] = useState(-1);
  const max = Math.max(0, ...rows.map((row) => row.value || 0));
  if (!rows.length || max === 0) return <p className="ck-empty">{empty}</p>;
  const shown = rows[hover];
  return (
    <div className="ck-cols" style={{ height }} onMouseLeave={() => setHover(-1)}>
      <span className="ck-cols-max">{max}</span>
      <div className="ck-cols-plot">
        {rows.map((row, index) => (
          <div
            key={row.key}
            className={`ck-col${hover === index ? ' is-on' : ''}`}
            onMouseEnter={() => setHover(index)}
            onFocus={() => setHover(index)}
            tabIndex={0}
            aria-label={`${row.tip || row.label} : ${row.value}`}
          >
            <i style={{ height: row.value ? `${Math.max(2, (row.value / max) * 100)}%` : 0 }} />
            <small>{index % labelEvery === 0 ? row.label : ''}</small>
          </div>
        ))}
      </div>
      {shown && (
        <div className="ck-tip" style={{ left: `${((hover + 0.5) / rows.length) * 100}%` }}>
          <b>{shown.value}</b> {shown.tip || shown.label}
        </div>
      )}
    </div>
  );
}

/** Liste classée avec barre fine (pays, villes, sources, pages). rows : [{ key, label, value, note }] */
export function RankBars({ rows, limit = 6, empty = 'Pas encore de donnée.' }) {
  const list = rows.filter((row) => row.value > 0).slice(0, limit);
  const max = Math.max(1, ...list.map((row) => row.value));
  if (!list.length) return <p className="ck-empty">{empty}</p>;
  return (
    <ul className="ck-rank">
      {list.map((row) => (
        <li key={row.key} title={`${row.label} : ${row.value}${row.note ? ` · ${row.note}` : ''}`}>
          <span className="ck-rank-label">{row.label}</span>
          <b>{row.value}</b>
          {row.note && <em>{row.note}</em>}
          <i><s style={{ width: `${(row.value / max) * 100}%` }} /></i>
        </li>
      ))}
    </ul>
  );
}

/** Courbe de rétention d'une vidéo. points : [{ mark: 0|25|50|75|100, n }] ; y = part de ceux qui l'ont lancée. */
export function Retention({ points }) {
  const [hover, setHover] = useState(-1);
  const start = points[0]?.n || 0;
  if (!start) return null;
  const W = 320;
  const H = 150;
  const pad = { l: 34, r: 14, t: 14, b: 26 };
  const x = (mark) => pad.l + (mark / 100) * (W - pad.l - pad.r);
  const y = (share) => pad.t + (1 - share) * (H - pad.t - pad.b);
  const coords = points.map((point) => ({ ...point, share: point.n / start, cx: x(point.mark), cy: y(point.n / start) }));
  const line = coords.map((point, index) => `${index ? 'L' : 'M'}${point.cx.toFixed(1)},${point.cy.toFixed(1)}`).join(' ');
  const area = `${line} L${x(100)},${y(0)} L${x(0)},${y(0)} Z`;
  const shown = coords[hover];
  return (
    <div className="ck-ret">
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Rétention : part des lecteurs encore là à chaque palier" onMouseLeave={() => setHover(-1)}>
        {[0, 0.5, 1].map((share) => (
          <g key={share}>
            <line x1={pad.l} x2={W - pad.r} y1={y(share)} y2={y(share)} className="ck-gridline" />
            <text x={pad.l - 6} y={y(share) + 4} textAnchor="end" className="ck-axis">{Math.round(share * 100)} %</text>
          </g>
        ))}
        {coords.map((point) => (
          <text key={point.mark} x={point.cx} y={H - 8} textAnchor="middle" className="ck-axis">{point.mark === 0 ? 'Lancé' : `${point.mark} %`}</text>
        ))}
        <path d={area} className="ck-ret-area" />
        <path d={line} className="ck-ret-line" />
        {shown && <line x1={shown.cx} x2={shown.cx} y1={pad.t} y2={y(0)} className="ck-cross" />}
        {coords.map((point, index) => (
          <g key={point.mark} onMouseEnter={() => setHover(index)}>
            <rect x={point.cx - 16} y={pad.t - 6} width={32} height={H - pad.t - pad.b + 12} fill="transparent" />
            <circle cx={point.cx} cy={point.cy} r={hover === index ? 6 : 4.5} className="ck-ret-dot" />
          </g>
        ))}
      </svg>
      {shown && (
        <div className="ck-tip" style={{ left: `${(shown.cx / W) * 100}%`, top: `${(shown.cy / H) * 100}%` }}>
          <b>{shown.n}</b> {shown.mark === 0 ? 'l’ont lancée' : `ont passé ${shown.mark} %`} · {Math.round(shown.share * 100)} %
        </div>
      )}
    </div>
  );
}
